/* eslint-disable */
/**
 * @file 编辑块的封装，包含块/组复制，删除，移动，编辑等方法
 */
import { DropdownButton, Icon, Toast } from '@seiue/ui'
import { copyToClipboard } from '@seiue/util'
import { useAtom } from 'jotai'
import { FC, useContext, useEffect, useRef, useState } from 'react'

import {
  BlockStateEnum,
  getChildrenBlocks,
  HeaderTypes,
} from 'packages/feature-utils/test-paper'

import {
  collapsedHeaderIdsAtom,
  focusedBlockAtom,
  highlightedBlockIdsAtom,
} from '../BlockList/atoms'
import { HandoutBlockListContext } from '../BlockList/context'
import { ButtonAddBlock } from '../ButtonAddBlock'

import { useDuplicate, useMove } from './hooks'
import {
  BlockContent,
  BlockMain,
  DragHandle,
  GroupButton,
  Spacer,
  Wrapper,
} from './StyledComponents'
import { BlockWrapperProps } from './types'
import { PaperBlock, BlockTypeEnum } from 'packages/sdks-next/growp'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { EditPaperContext } from '../../pages/EditPaper/context'
import styled from 'styled-components'
import { getTheme } from '@/utils/theme'

/**
 * 编辑块的封装，包含块/组复制，删除，移动，编辑等方法
 *
 * @param {BlockWrapperProps} props - BlockWrapperProps
 * @returns Component
 */
export const BlockWrapper: FC<BlockWrapperProps> = ({
  block: BlockBody,
  footers,
  blockData,
  dragSource,
}) => {
  const theme = getTheme()
  const {
    paper,
    blocks,
    blockMovable,
    initialState,
    availableStates,
    setGroupDraggingId,
    isStudent,
    isPublished,
    hasAnswered,
    canEdit,
    api: { deleteBlock, updateBlock },
  } = useContext(HandoutBlockListContext)
  const { limitLevel } = useContext(EditPaperContext)

  const [focusedBlock, setFocusedBlock] = useAtom(focusedBlockAtom)
  const [hightedBlockIds, setHightedBlockIds] = useAtom(highlightedBlockIdsAtom)

  // 默认设置拖拽为块拖拽
  const dragSourceRef = useRef<HTMLDivElement>(null)
  const dragGroupRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (dragSourceRef.current && blockMovable) {
      dragSource(dragSourceRef)
    }
  }, [dragSource, dragSourceRef, blockMovable])

  // 成组按钮点击，会唤起 Dropdown，如果当前有高亮的组则取消
  const onVisibleChange = (visible: boolean) => {
    setFocusedBlock({
      id: -1,
      state: initialState,
    })

    if (!visible) {
      setHightedBlockIds([])
    } else {
      const childrenHeaderIds = getChildrenBlocks(
        blockData.id,
        (blocks || []).filter((b) => HeaderTypes.includes(b.type))
      ).map((b) => b.id)

      setHightedBlockIds(childrenHeaderIds)
    }
  }

  // 块的当前状态
  const [state, setState] = useState<BlockStateEnum>(initialState)
  const onChangeState = (s: BlockStateEnum) => {
    // 只有在切换的状态在 availableStates 中才允许切换
    if (availableStates.includes(s)) {
      setState(s)

      if (s === BlockStateEnum.Display) {
        setFocusedBlock({
          id: -1,
          state: BlockStateEnum.Display,
        })
      } else {
        setFocusedBlock({ id: blockData.id, state: s })
      }
    }
  }

  const [solutionExpanded, setSolutionExpanded] = useState<boolean>(false)
  const onSolutionClick = (expanded?: boolean) =>
    setSolutionExpanded(!!expanded)

  // 同一时间内只能有一个块选中并编辑
  useEffect(() => {
    if (focusedBlock.id !== blockData.id) {
      setState(initialState)
    } else if (availableStates.includes(focusedBlock.state)) {
      setState(focusedBlock.state)
    }
  }, [focusedBlock, blockData.id, initialState])

  // 块数据暂存
  const [tempBlockData, setTempBlockData] = useState<PaperBlock>(blockData)
  useEffect(() => {
    setTempBlockData(blockData)
  }, [blockData])

  // 修改暂存的数据，如果传入 immediateUpdate 则立即更新
  const onChangeTempBlockData = (
    data: Partial<PaperBlock>,
    options?: {
      immediateUpdate: boolean
    }
  ) => {
    ;(options?.immediateUpdate ? updateBlock : setTempBlockData)({
      ...tempBlockData,
      ...data,
    })
  }

  // 取消编辑恢复初始数据
  const onCancel = async () => {
    // 问题块问题为空时删除
    if (
      blockData.type === BlockTypeEnum.Question &&
      !blockData.questions?.find((q) => q.parentId === 0)
    ) {
      await onDelete(undefined, false)
    } else {
      setTempBlockData(blockData)
    }
  }

  // 保存除问题的块，如果内容为空(无需 trim)，则删除该块
  const onSave = async () => {
    if (
      tempBlockData.type !== BlockTypeEnum.Question &&
      !tempBlockData.content
    ) {
      await onDelete({ onlyDeleteSelf: true }, false)
    } else {
      await updateBlock(tempBlockData)
    }
  }

  // 复制块/组
  const { onDuplicate } = useDuplicate(blockData, blocks || [])

  // 移动块相关逻辑（上移/下移/移动到）
  const { canMoveUp, canMoveDown, onMove } = useMove(blockData, blocks || [])

  const [collapsedHeaderIds, setCollapsedHeaderIds] = useAtom(
    collapsedHeaderIdsAtom
  )

  // 组的展开与收起
  const toggleCollapse = (setCollapsed = true) => {
    const childrenHeaderIds = getChildrenBlocks(
      blockData.id,
      (blocks || []).filter((b) => HeaderTypes.includes(b.type))
    ).map((b) => b.id)

    let newIds = collapsedHeaderIds.slice()

    if (setCollapsed) {
      newIds.push(...childrenHeaderIds)
    } else {
      newIds = newIds.filter((i) => !childrenHeaderIds.includes(i))
    }

    setCollapsedHeaderIds(newIds)
  }

  const onGroupDragStart = () => {
    // 页面上的最后一个组在 dragStart 时立即收起可能因为导致 drag 终止，所以延迟收起子元素
    setTimeout(() => {
      toggleCollapse(true)
      // 设置当前拖拽模式为组拖拽
      setGroupDraggingId(blockData.id)
    }, 200)
  }

  const onGroupDragEnd = () => {
    setTimeout(() => {
      toggleCollapse(false)
    }, 500)
  }

  const onDelete = async (
    options = { onlyDeleteSelf: true },
    showToast = false
  ) => {
    await deleteBlock({
      blockId: blockData.id,
      onlyDeleteSelf: options.onlyDeleteSelf,
    })
    setFocusedBlock({
      id: -1,
      state: BlockStateEnum.Display,
    })
    if (showToast) {
      Toast.success('删除成功')
    }
  }

  // 复制块/组链接
  const onCopyLink = () => {
    const result = copyToClipboard(
      `##{${blockData.paperId}_${blockData.paperId}_${blockData.hash}}##`
    )

    if (result) {
      Toast.success('复制成功')
    }
  }

  const blockProps = {
    state,
    solutionExpanded,
    onSolutionClick,
    onChangeState,
    blockData: tempBlockData,
    toggleCollapse,
    onChangeBlockData: onChangeTempBlockData,
    onCancel,
    onSave,
    onDelete,
    canMoveUp,
    canMoveDown,
    onMoveUp: () => onMove('up', 'block'),
    onMoveDown: () => onMove('down', 'block'),
    onCopyLink,
    isStudent,
    isPublished,
    hasAnswered,
    canEdit,
    onDuplicate,
    limitLevel,
  }

  return (
    <Wrapper
      $collapsed={collapsedHeaderIds.includes(Number(blockData.parentId))}
      data-block-id={blockData.id}
      data-block-weight={blockData.weight}
      data-block-parent={blockData.parentId}
      id={`${blockData.id}`}
    >
      {HeaderTypes.includes(blockData.type) && blockMovable ? (
        <DropdownButton
          label={
            <GroupButton
              ref={dragGroupRef}
              onMouseEnter={() => {
                dragSource(dragGroupRef)
              }}
              onDragStart={onGroupDragStart}
              onDragEnd={onGroupDragEnd}
              draggable={true}
            >
              <GrowpIcon
                name="Menus"
                size={14}
              />
            </GroupButton>
          }
          showArrow={false}
          beforeCallback={() => {
            setHightedBlockIds([])
            return true
          }}
          onVisibleChange={onVisibleChange}
          labelButtonProps={{
            type: 'text',
            size: 'xs',
          }}
          trigger="click"
          items={[
            ...(limitLevel < 2 && canEdit
              ? [
                  {
                    label: '复制副本',
                    render: () => {
                      return (
                        <DropDownItemLabel
                          onClick={() => onDuplicate({ range: 'group' })}
                        >
                          <GrowpIcon name="Copy" />
                          复制副本
                        </DropDownItemLabel>
                      )
                    },
                  },
                ]
              : []),
            {
              label: '上移',
              disabled: !canMoveUp,
              render: () => {
                return (
                  <DropDownItemLabel onClick={() => onMove('up', 'group')}>
                    <GrowpIcon
                      name="UpMove"
                      color={canMoveUp ? theme.text._1 : theme.text._4}
                    />
                    上移
                  </DropDownItemLabel>
                )
              },
            },
            {
              label: '下移',
              disabled: !canMoveDown,
              render: () => {
                return (
                  <DropDownItemLabel onClick={() => onMove('down', 'group')}>
                    <GrowpIcon
                      name="DownMove"
                      color={canMoveDown ? theme.text._1 : theme.text._4}
                    />
                    下移
                  </DropDownItemLabel>
                )
              },
            },
            ...(limitLevel < 2 && canEdit
              ? [
                  {
                    label: '删除组',
                    render: () => {
                      return (
                        <DropDownItemLabel
                          onClick={() => onDelete({ onlyDeleteSelf: false })}
                        >
                          <GrowpIcon name="Delete" />
                          删除组
                        </DropDownItemLabel>
                      )
                    },
                  },
                ]
              : []),
          ]}
        />
      ) : blockMovable ? (
        <Spacer />
      ) : null}

      <BlockMain>
        {blockMovable ? (
          <DragHandle
            ref={dragSourceRef}
            onMouseEnter={() => {
              dragSource(dragSourceRef)
            }}
            draggable={state !== BlockStateEnum.Editing}
          >
            <BlockContent
              $state={state}
              $highlighted={
                hightedBlockIds.includes(Number(blockData.parentId)) ||
                hightedBlockIds.includes(Number(blockData.id))
              }
              $borderOnHover={!isStudent}
            >
              {BlockBody(blockProps)}

              {footers
                .filter((f) => f.visible({ state }))
                .map((f, index) => f.render({ ...blockProps, key: index }))}
            </BlockContent>

            {/* 可切换态包含「编辑态」，且当前态不为「编辑态」的时候，展示新增按钮 */}
            {canEdit && state !== BlockStateEnum.Editing ? (
              <ButtonAddBlock
                position={(blockData.weight || 0) + 1}
                parentId={blockData.parentId || 0}
              />
            ) : null}
          </DragHandle>
        ) : null}
      </BlockMain>
    </Wrapper>
  )
}

const DropDownItemLabel = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`
